import { useState, useEffect, useMemo } from 'react';
import omit from 'lodash/omit';
import { useObserver } from 'mobx-react-lite';
import { useTheme } from 'styled-components';

import useDebounce from 'shared/hooks/use-debounce';

import useViewport from 'hooks/use-viewport';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';
import useCart from 'hooks/use-cart';
import { useModals, ModalNames } from 'src/components/modals';
import { clearNudgeIntentMarker } from 'src/components/modals/phone-verification-nudge/nudge-intent-marker';
import { clearEmbeddedOAuthHandoff, readEmbeddedOAuthHandoff } from 'src/oauth/embedded-oauth-channel';
import { EMBEDDED_OAUTH_POPUP_HANDOFF_FLAG } from 'src/oauth/embedded-oauth-handoff-flag';
import { reportEmbeddedOAuthSender } from 'src/oauth/oauth-flow-nonce';
import { classifyEmbeddedOAuthSender } from 'src/oauth/embedded-oauth-sender-kind';
import { consumeOAuthPopupSource, getOAuthPopupHandleCount, isOAuthPopupSource } from 'src/oauth/oauth-popup-source';
import { EMBEDDED_OAUTH_MESSAGE_REQUIRE_POPUP_SOURCE_FLAG } from 'src/oauth/embedded-oauth-message-source-flag';
import { useFlags } from 'launchdarkly-react-client-sdk';

export function useEmbeddedEvents() {
  const UI = useUI();
  const User = useUser();
  const Cart = useCart();
  const { closeModal } = useModals();
  const isEmbedded = useObserver(() => UI.isEmbedded);
  const isIframe = useObserver(() => UI.isIframe);
  const flags = useFlags();
  const isEmbeddedOAuthPopupHandoffEnabled = flags[EMBEDDED_OAUTH_POPUP_HANDOFF_FLAG] ?? false;
  const requirePopupSource = flags[EMBEDDED_OAUTH_MESSAGE_REQUIRE_POPUP_SOURCE_FLAG] ?? false;
  const [initialized, setInitialized] = useState(false);
  const [currentViewport, setCurrentViewport] = useState({});
  const { customized } = useTheme();

  useEffect(() => {
    if (!isEmbedded) {
      return undefined;
    }
    function handler(event) {
      let payload = {};
      try {
        payload = JSON.parse(event.data);
      } catch (_e) {
        // console.log('Unable to parse iframe data', event.data);
      }

      if (payload.event === 'embed:events:viewport') {
        const { viewport } = payload;
        const shouldUseOffsetFromScrollTop = viewport.offsetFromWindowScrollTop !== undefined;
        if (shouldUseOffsetFromScrollTop) {
          viewport.offset = Math.max(0, viewport.offsetFromWindowScrollTop);
        }
        setCurrentViewport(viewport);
      } else if (payload.event === 'embed:events:scroll-start') {
        UI.isScrolling = true;
      } else if (payload.event === 'embed:events:scroll-end') {
        UI.isScrolling = false;
      } else if (payload.event === 'embed:previewMode') {
        UI.previewMode = payload.previewMode;
      } else if (payload.event === 'embed:ready') {
        UI.isIframeSDK = true;
      } else if (payload.event === 'embed:registerClickHandler:login') {
        UI.isUsingExternalClickHandlerLogin = true;
      } else if (payload.event === 'embed:setUserDetails') {
        const userDetails = omit(payload.userDetails, [`medicalCard`]);
        User.setUserDetailsFromExternalSource(userDetails);
        Cart.setMedicalCard(payload.userDetails?.medicalCard);
      } else if (payload.event === 'embed:setParentUrl') {
        UI.iframeParentUrl = payload.url;
        UI.iframeParentHref = payload.href;
      } else if (payload.event === 'embed:oAuthLogin') {
        const sameOrigin = event.origin === window.location.origin;
        const isPopupSource = isOAuthPopupSource(event.source);
        reportEmbeddedOAuthSender(
          sameOrigin,
          isPopupSource,
          classifyEmbeddedOAuthSender(event.source, isPopupSource),
          getOAuthPopupHandleCount()
        );
        if (requirePopupSource && (!sameOrigin || !isPopupSource)) {
          return;
        }
        if (sameOrigin && isPopupSource) {
          consumeOAuthPopupSource(event.source);
        }
        User.updateToken(payload.token);
        if (payload.isNewUser) {
          clearNudgeIntentMarker();
        }
        // Either modal could be open, so attempt to close both
        closeModal(ModalNames.login);
        closeModal(ModalNames.signUp);
      }
    }

    window.addEventListener('message', handler);

    if (!initialized) {
      setInitialized(true);
      UI.embeddedEventDispatcher(`iframe:ready`, {
        primaryColor: customized.colors.buttonsLinks,
      });
    }
    return () => {
      window.removeEventListener('message', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requirePopupSource]);

  useEffect(() => {
    if (!isEmbedded || !isEmbeddedOAuthPopupHandoffEnabled) {
      return undefined;
    }

    // Fallback receiver for the SSO popup's OAuth handoff: storage events fire in this iframe
    // when another same-origin window writes the dedicated key. This is intentionally gated by
    // the same flag that marks the popup flow, so disabled locations preserve legacy behavior.
    function storageHandler(event) {
      const payload = readEmbeddedOAuthHandoff(event);
      if (!payload) {
        return;
      }

      User.updateToken(payload.token);
      if (payload.isNewUser) {
        clearNudgeIntentMarker();
      }
      closeModal(ModalNames.login);
      closeModal(ModalNames.signUp);
      clearEmbeddedOAuthHandoff();
    }

    window.addEventListener('storage', storageHandler);
    return () => window.removeEventListener('storage', storageHandler);
  }, [User, closeModal, isEmbedded, isEmbeddedOAuthPopupHandoffEnabled]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const currentViewportMemo = useMemo(() => currentViewport, [
    currentViewport.height,
    currentViewport.width,
    currentViewport.offset,
    currentViewport.parentOffset,
    currentViewport.offsetTop,
  ]);
  const viewport = useDebounce(currentViewportMemo, 90);
  const { setViewport } = useViewport();

  useEffect(() => {
    if (!isIframe) {
      return;
    }

    setViewport(viewport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewport.height, viewport.width, viewport.offset, viewport.parentOffset, viewport.offsetTop]);
}
